import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { AgentCallApiService } from '../services/agent-call-api.service';
import { AgentCallCrmContextResponse, TicketContext } from '../models/agent-call.model';
import { CALL_CONTEXT_DEAL_STATUS_LABELS, labelFrom } from '../call-labels';

@Component({
  selector: 'app-call-crm-context-panel',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './call-crm-context-panel.component.html'
})
export class CallCrmContextPanelComponent implements OnChanges {
  @Input() callId: number | null = null;

  loading = false;
  errorMsg: string | null = null;
  context: AgentCallCrmContextResponse | null = null;
  collapsed = false;

  private lastFetchedId: number | null = null;

  constructor(
    private readonly api: AgentCallApiService,
    private readonly router: Router
  ) {}

  dealStatusLabel(status: string | null | undefined): string {
    return labelFrom(CALL_CONTEXT_DEAL_STATUS_LABELS, status, '');
  }

  /**
   * Etiqueta legible del estado del ticket. El backend la resuelve en
   * `statusLabel`; `status` sigue siendo el id numérico y nunca se pinta,
   * así que sin etiqueta no se muestra el chip.
   */
  ticketStatusLabel(t: TicketContext): string {
    return t.statusLabel?.trim() ?? '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!('callId' in changes)) return;
    if (this.callId == null) {
      this.context = null;
      this.lastFetchedId = null;
      this.errorMsg = null;
      return;
    }
    if (this.callId === this.lastFetchedId) return;
    this.cargar();
  }

  cargar(): void {
    if (this.callId == null) return;
    this.loading = true;
    this.errorMsg = null;
    const id = this.callId;
    this.api.getCrmContext(id).subscribe({
      next: (res) => {
        this.context = res;
        this.lastFetchedId = id;
        this.loading = false;
      },
      error: (err) => {
        if (err.status === 404) {
          this.context = {};
          this.lastFetchedId = id;
        } else {
          this.errorMsg = err.error?.message || 'No se pudo cargar el contexto CRM.';
        }
        this.loading = false;
      }
    });
  }

  toggleCollapsed(): void { this.collapsed = !this.collapsed; }

  hasAnyContent(): boolean {
    if (!this.context) return false;
    return !!this.context.contact || !!this.context.company
      || (this.context.deals?.length ?? 0) > 0
      || (this.context.tickets?.length ?? 0) > 0;
  }

  goToContact(id?: number): void {
    if (!id) return;
    this.router.navigate(['/contactos/editar', id]);
  }

  goToCompany(id?: number): void {
    if (!id) return;
    this.router.navigate(['/empresas/editar', id]);
  }

  goToDeal(id?: number): void {
    if (!id) return;
    this.router.navigate(['/deals/editar', id]);
  }

  goToTicket(id?: number): void {
    if (!id) return;
    this.router.navigate(['/tickets/editar', id]);
  }

  trackById = (_: number, item: { id: number }) => item.id;

  contactInitials(): string {
    const name = this.context?.contact?.name?.trim();
    if (!name) return '?';
    const parts = name.split(/\s+/).slice(0, 2);
    return parts.map(p => p[0]?.toUpperCase() ?? '').join('') || '?';
  }
}
